import { GoogleOAuthProvider } from "@react-oauth/google";
import { Refine } from "@refinedev/core";
import React, { useEffect } from "react";
import { DevtoolsPanel, DevtoolsProvider } from "@refinedev/devtools";
import { RefineKbar, RefineKbarProvider } from "@refinedev/kbar";

import {
  ErrorComponent,
  RefineSnackbarProvider,
  useNotificationProvider,
} from "@refinedev/mui";

import CssBaseline from "@mui/material/CssBaseline";
import GlobalStyles from "@mui/material/GlobalStyles";
import routerBindings, {
  DocumentTitleHandler,
  UnsavedChangesNotifier,
} from "@refinedev/react-router-v6";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
} from "react-router-dom";
import { authProvider } from "./authProvider";
import { AppIcon } from "./components/app-icon";
import { ColorModeContextProvider } from "./contexts/color-mode";
import { dataProvider } from "./dataProvider";
import { BatchList } from "./pages/batch/list";
import { BrandList } from "./pages/brand/list";
import { JobList } from "./pages/job/list";
import { Login } from "./pages/login";
import { JobView } from "./pages/job/show";
import AuthorizationPage from "./pages/authorization";
import PhrasebookEditorPage from "./pages/phrasebook-editor";
import RedirectIfAuthenticated from "./components/authentication/redirect-if-authenticated";
import RedirectIfNotAuthenticated from "./components/authentication/redirect-if-not-authenticated";
import AuthCheck from "./components/authentication/auth-check";
import ClientBatches from "./pages/client/batches";
import UserTheme from "./components/authentication/user-theme";
import ClientBatch from "./pages/client/batch";
import EnforcePermissions from "./components/authentication/enforce-permissions";
import ProfilePage from "./pages/user/profile";
import InitialRedirect from "./components/common/initial-redirect";
import { ForceChangePassword } from "./pages/force-change-password";
import { ForcePasswordGuard } from "./components/common/force-password-guard";
import { TermsGuard } from "./components/common/terms-guard";
import { AcceptTerms } from "./pages/accept-terms";
import { ForgotPassword } from "./pages/forgotPassword";
import { ResetPassword } from "./pages/resetPassword";

function DomainRedirect({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    if (window.location.hostname === "fashionwords.fly.dev") {
      const newUrl = `https://mycontent.fashionwords.eu${window.location.pathname}${window.location.search}`;
      window.location.replace(newUrl);
    }
  }, []);

  return children;
}

function App() {
  return (
    <BrowserRouter>
      <DomainRedirect>
        <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
          <RefineKbarProvider>
            <ColorModeContextProvider>
              <CssBaseline />
              <GlobalStyles
                styles={{ html: { WebkitFontSmoothing: "auto" } }}
              />
              <RefineSnackbarProvider>
                <DevtoolsProvider>
                  <Refine
                    dataProvider={dataProvider()}
                    notificationProvider={useNotificationProvider}
                    authProvider={authProvider}
                    routerProvider={routerBindings}
                    resources={[
                      {
                        name: "brand",
                        list: "/brands",
                        meta: {
                          hide: true,
                        },
                      },
                      {
                        name: "batch",
                        list: "/brand/:brandId",
                        meta: {
                          parent: "brand",
                          hide: true,
                        },
                      },
                      {
                        name: "job",
                        show: "/job/:id",
                        list: "/batch/:batchId",
                        meta: {
                          hide: true,
                        },
                      },
                      {
                        name: "playground",
                        list: "/admin/playground",
                      },
                      {
                        name: "authorization",
                        list: "/admin/authorization",
                      },
                      {
                        name: "phrasebook-editor",
                        list: "/admin/phrasebook-editor",
                      },
                      {
                        name: "home",
                        list: "/",
                      },
                    ]}
                    options={{
                      syncWithLocation: true,
                      warnWhenUnsavedChanges: true,
                      useNewQueryKeys: true,
                      projectId: "q3s2uf-bblcQs-bIFOwZ",
                      title: { text: "Fashion Words", icon: <AppIcon /> },
                    }}
                  >
                    <UserTheme>
                      <Routes>
                        <Route
                          element={
                            <AuthCheck>
                              <RedirectIfAuthenticated>
                                <Outlet />
                              </RedirectIfAuthenticated>
                            </AuthCheck>
                          }
                        >
                          <Route path="login" element={<Login />} />
                          <Route
                            path="forgot-password"
                            element={<ForgotPassword />}
                          />
                          <Route
                            path="reset-password"
                            element={<ResetPassword />}
                          />
                        </Route>

                        <Route
                          element={
                            <AuthCheck>
                              <RedirectIfNotAuthenticated>
                                <Outlet />
                              </RedirectIfNotAuthenticated>
                            </AuthCheck>
                          }
                        >
                          {/* Force password change route - must be accessible without guards */}
                          <Route
                            path="/force-change-password"
                            element={<ForceChangePassword />}
                          />

                          {/* Terms acceptance route - must be accessible without TermsGuard */}
                          <Route
                            path="/accept-terms"
                            element={<AcceptTerms />}
                          />

                          {/* All other authenticated routes wrapped with ForcePasswordGuard and TermsGuard */}
                          <Route
                            element={
                              <ForcePasswordGuard>
                                <TermsGuard>
                                  <Outlet />
                                </TermsGuard>
                              </ForcePasswordGuard>
                            }
                          >
                            <Route index element={<InitialRedirect />} />

                            <Route path="/profile" element={<ProfilePage />} />

                            <Route path="/brands" element={<BrandList />} />

                            <Route
                              path="brand/:brandId"
                              element={<BatchList />}
                            />

                            <Route path="batch/:batchId" element={<JobList />} />

                            <Route path="job/:jobId" element={<JobView />} />

                            <Route
                              element={
                                <EnforcePermissions role="client">
                                  <Outlet />
                                </EnforcePermissions>
                              }
                            >
                              <Route path="/review" element={<ClientBatches />} />

                              <Route
                                path="/review/:batchId"
                                element={<ClientBatch />}
                              />
                            </Route>

                            <Route
                              element={
                                <EnforcePermissions role="admin">
                                  <Outlet />
                                </EnforcePermissions>
                              }
                            >
                              <Route path="/admin">
                                <Route
                                  path="playground"
                                  element={<Navigate to="/" replace />}
                                />
                                <Route
                                  path="playground/*"
                                  element={<Navigate to="/" replace />}
                                />
                                <Route
                                  path="authorization"
                                  element={<AuthorizationPage />}
                                />
                                <Route
                                  path="phrasebook-editor"
                                  element={<PhrasebookEditorPage />}
                                />
                                <Route
                                  index
                                  element={<Navigate to="/brands" />}
                                />
                              </Route>
                            </Route>
                          </Route>
                        </Route>

                        <Route path="*" element={<ErrorComponent />} />
                      </Routes>
                    </UserTheme>

                    <RefineKbar />
                    <UnsavedChangesNotifier />
                    <DocumentTitleHandler
                      handler={(options) =>
                        options.autoGeneratedTitle.replace(
                          "Refine",
                          "MyContent – Fashion Words"
                        )
                      }
                    />
                  </Refine>
                  <DevtoolsPanel />
                </DevtoolsProvider>
              </RefineSnackbarProvider>
            </ColorModeContextProvider>
          </RefineKbarProvider>
        </GoogleOAuthProvider>
      </DomainRedirect>
    </BrowserRouter>
  );
}

export default App;
